import React, { Dispatch, useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action } from 'redux';

import { CallStatus } from '../../../lib/enums';
import { AuthLoginData } from '../../../lib/models/auth/types';
import { CreatePlanPaymentParams, PurchasePaymentTypeEnum } from '../../../lib/models/crm/types';
import { RootState } from '../../../lib/store';
import { autoLogin as autoLoginCall, logout } from '../../../lib/store/common-actions';
import { createPlanPayment } from '../../../lib/store/crm';
import { getSelectedTradingPlan, preparePaymentURLs } from '../../../lib/store/crm/helpers';
import { CRMCall } from '../../../lib/store/crm/types';
import { hasValidCookie } from '../../../lib/store/selectors';
import {
  getPaymentProvider,
  getPaymentReferenceId,
  getPurchasedPlanId,
} from '../../../lib/util/url-helpers';

import { GTM_CATEGORIES, GTM_EVENTS, GTM_PAYMENT_FAILED } from '../../../constants/gtm';
import { hashSHA256 } from '../../../util/hash';
import { preparePaymentErrorPathByPaymentProvider } from '../../../util/helpers';
import { UI_ROUTES } from '../../../util/routes';
import ErrorLayout from '../ErrorLayout';

const PaymentPlanError = (props: ReduxProps): JSX.Element => {
  const navigate = useNavigate();
  const {
    isAppLoaded,
    isCookieValid,
    createPayment,
    individualId,
    selectedTradingPlanID,
    isPlanPaymentsReady,
    paymentGatewayURL,
    appLogOut,
    autoLogin,
    userId,
    userEmail,
    tradingPlans,
  } = props;

  const hasTrackedPaymentFailed = useRef<boolean>(false);
  const purchasedPlanId = getPurchasedPlanId();
  const plan = tradingPlans.find((p) => p.id === purchasedPlanId);

  // GTM: Track payment failure (fire only once)
  useEffect(() => {
    if (!hasTrackedPaymentFailed.current) {
      hasTrackedPaymentFailed.current = true;
      const paymentReferenceId = getPaymentReferenceId();
      const pushEvent = async () => {
        const hashedEmail = await hashSHA256(userEmail || '');
        TagManager.dataLayer({
          dataLayer: {
            event: GTM_EVENTS.PAYMENT_FAILED,
            event_category: GTM_CATEGORIES.CHALLENGE_PURCHASE,
            event_action: GTM_PAYMENT_FAILED.ACTION,
            event_label: GTM_PAYMENT_FAILED.LABEL,
            client_id: userId,
            user_email_hashed: hashedEmail,
            transaction_id: paymentReferenceId,
            challenge_name: plan?.phase,
            buying_power: plan?.daily_hard_bp,
            currency: plan?.daily_hard_ccy,
          },
        });
      };
      pushEvent();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPlanPaymentsReady && paymentGatewayURL) {
      window.open(paymentGatewayURL as string, '_self');
    }
  }, [isPlanPaymentsReady, paymentGatewayURL]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    if (!isAppLoaded) {
      return;
    }

    // Get paymentProvider from URL (query param)
    const paymentProvider = getPaymentProvider();

    if (!isCookieValid || !paymentProvider) {
      appLogOut();
      return;
    }

    if (!individualId || !selectedTradingPlanID) {
      autoLogin({
        successURL: preparePaymentErrorPathByPaymentProvider(paymentProvider),
      } as AuthLoginData);
      return;
    }

    createPayment({
      individual_id: individualId as number,
      trading_plan_id: selectedTradingPlanID!,
      payment_provider: paymentProvider,
      ...preparePaymentURLs(
        PurchasePaymentTypeEnum.Plan,
        UI_ROUTES.paymentSuccess,
        UI_ROUTES.paymentError,
        UI_ROUTES.paymentError,
      ),
    });
  };

  return (
    <ErrorLayout
      handleClose={() => {
        navigate(-1);
      }}
      handleSubmit={handleSubmit}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  isAppLoaded: state.app.loaded,
  isCookieValid: hasValidCookie(state),
  individualId: state.crm.individualOrCompanyEmployeeId,
  selectedTradingPlanID: getSelectedTradingPlan(state.crm)?.id,
  paymentGatewayURL: state.crm.paymentResponseData.gateway_url,
  isPlanPaymentsReady:
    state.crm.statusByCall[CRMCall.createPlanPayment]?.status === CallStatus.READY,
  userId: state.auth.enroll.userId,
  userEmail: state.crm.individualExtendedInfo?.email,
  tradingPlans: state.crm.tradingPlans,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  autoLogin: (data: AuthLoginData): void => dispatch(autoLoginCall(data)),
  createPayment: (params: CreatePlanPaymentParams) => dispatch(createPlanPayment(params)),
  appLogOut: () => dispatch(logout()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(PaymentPlanError);
